import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowUp, X } from 'lucide-react';
import InnerHeader from '../components/common/InnerHeader/InnerHeader';
import InnerFooter from '../components/common/InnerFooter/InnerFooter';
import PageTitleBar from '../components/common/PageTitleBar/PageTitleBar';
import './Analytics.css';
import './styles/pages-common.css';
import footerImgNew from '../assets/footer-img.png';

// Reusable custom TrendChart component using responsive SVG with hover interactions
const TrendChart = ({ title, timeframe, data, type }) => {
    const [hoverIndex, setHoverIndex] = useState(null);
    const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
    const svgRef = useRef(null);

    // Helper to format date/hour labels
    const formatDateLabel = (dateStr) => {
        if (!dateStr) return '';
        const parts = dateStr.split('-');
        if (parts.length < 3) return dateStr;
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const monthIdx = parseInt(parts[1], 10) - 1;
        const day = parseInt(parts[2], 10);
        return `${day} ${months[monthIdx]}`;
    };

    const formatHourLabel = (dateStr) => {
        if (!dateStr || dateStr.length < 16) return '';
        return dateStr.substring(11, 16);
    };

    // Calculate paths and ticks dynamically
    if (data === null) {
        return (
            <div className="analytics-card">
                <div className="analytics-card-header">
                    <div className="analytics-card-title-group">
                        <h3 className="analytics-card-title">{title}</h3>
                        <span className="analytics-card-subtitle">&nbsp;</span>
                    </div>
                    <span className="analytics-card-badge">[{timeframe}]</span>
                </div>
                <div className="skeleton-loader" style={{ height: '220px' }}>
                    <div className="skeleton-chart"></div>
                </div>
            </div>
        );
    }

    const maxPM = Math.max(...(data || []).map(d => Math.max(d.pm_before || 0, d.pm_after || 0)), 50);
    const roundedMax = Math.ceil(maxPM / 40) * 40 || 240;
    const tickStep = roundedMax / 4;
    const ticks = [0, tickStep, tickStep * 2, tickStep * 3, roundedMax];

    const points = (data || []).map((d, idx) => {
        const x = 50 + (400 / (data.length - 1 || 1)) * idx;
        const yBefore = 200 - (170 * ((d.pm_before || 0) / roundedMax));
        const yAfter = 200 - (170 * ((d.pm_after || 0) / roundedMax));
        return {
            x,
            yBefore,
            yAfter,
            pm_before: d.pm_before,
            pm_after: d.pm_after,
            rawDate: d.report_date || d.report_hour
        };
    });

    const orangePath = points.map((p, idx) => (idx === 0 ? 'M' : 'L') + ` ${p.x},${p.yBefore}`).join(' ');
    const greenPath = points.map((p, idx) => (idx === 0 ? 'M' : 'L') + ` ${p.x},${p.yAfter}`).join(' ');

    const orangeFillPath = points.length > 0 ? `${orangePath} L ${points[points.length - 1].x},200 L ${points[0].x},200 Z` : '';
    const greenFillPath = points.length > 0 ? `${greenPath} L ${points[points.length - 1].x},200 L ${points[0].x},200 Z` : '';

    const lastPoint = points[points.length - 1] || { x: 450, yBefore: 120, yAfter: 170 };
    const orangeEndVal = data && data.length > 0 ? (data[data.length - 1].pm_before || 0).toFixed(0) : '0';
    const greenEndVal = data && data.length > 0 ? (data[data.length - 1].pm_after || 0).toFixed(0) : '0';

    // X-axis labels logic
    let xLabels = [];
    if (data && data.length > 0) {
        if (type === '24h') {
            const N = data.length;
            const indices = [0, Math.floor(N * 0.25), Math.floor(N * 0.5), Math.floor(N * 0.75), N - 1];
            const uniqueIndices = [...new Set(indices)].filter(idx => idx >= 0 && idx < N);
            xLabels = uniqueIndices.map(idx => ({
                text: formatHourLabel(data[idx].report_hour),
                x: 50 + (400 / (N - 1 || 1)) * idx
            }));
        } else if (type === '7d') {
            xLabels = data.map((d, idx) => ({
                text: formatDateLabel(d.report_date),
                x: 50 + (400 / (data.length - 1 || 1)) * idx
            }));
        } else {
            // 30d
            const N = data.length;
            const indices = [0, Math.floor(N * 0.25), Math.floor(N * 0.5), Math.floor(N * 0.75), N - 1];
            const uniqueIndices = [...new Set(indices)].filter(idx => idx >= 0 && idx < N);
            xLabels = uniqueIndices.map(idx => ({
                text: formatDateLabel(data[idx].report_date),
                x: 50 + (400 / (N - 1 || 1)) * idx
            }));
        }
    }

    const handleMouseMove = (e) => {
        if (!svgRef.current || points.length === 0) return;
        const rect = svgRef.current.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        const localX = ((clientX - rect.left) / rect.width) * 500;

        const pct = (localX - 50) / 400;
        const idx = Math.max(0, Math.min(points.length - 1, Math.round(pct * (points.length - 1))));
        setHoverIndex(idx);

        const cardRect = e.currentTarget.getBoundingClientRect();
        setTooltipPos({
            x: clientX - cardRect.left,
            y: clientY - cardRect.top - 12
        });
    };

    const handleMouseLeave = () => {
        setHoverIndex(null);
    };

    const getTooltipDate = () => {
        if (hoverIndex === null || !points[hoverIndex]) return '';
        const raw = points[hoverIndex].rawDate;
        if (type === '24h') {
            if (raw.length >= 16) {
                return `${raw.substring(11, 16)} (${formatDateLabel(raw.substring(0, 10))})`;
            }
            return raw;
        }
        return formatDateLabel(raw);
    };

    return (
        <div
            className="analytics-card"
            onMouseMove={handleMouseMove}
            onTouchMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onTouchEnd={handleMouseLeave}
        >
            <div className="analytics-card-header">
                <div className="analytics-card-title-group">
                    <h3 className="analytics-card-title">{title}</h3>
                    <div className="analytics-legend-group" style={{ margin: 0, justifyContent: 'flex-start', marginTop: 4 }}>
                        <div className="analytics-legend-item">
                            <span className="analytics-legend-color analytics-legend-orange"></span>
                            Before Filtration
                        </div>
                        <div className="analytics-legend-item">
                            <span className="analytics-legend-color analytics-legend-green"></span>
                            After Filtration
                        </div>
                    </div>
                </div>
                <span className="analytics-card-badge">[{timeframe}]</span>
            </div>

            <div className="analytics-chart-container">
                {data.length === 0 ? (
                    <div style={{ color: '#8a99ad', fontSize: 13, textAlign: 'center', paddingTop: 80 }}>
                        No trend telemetry data found
                    </div>
                ) : (
                    <svg ref={svgRef} viewBox="0 0 500 240" className="analytics-chart-svg" preserveAspectRatio="none">
                        <defs>
                            <linearGradient id={`orangeGrad-${type}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#E99924" stopOpacity="0.4" />
                                <stop offset="100%" stopColor="#E99924" stopOpacity="0.0" />
                            </linearGradient>
                            <linearGradient id={`greenGrad-${type}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#22c55e" stopOpacity="0.4" />
                                <stop offset="100%" stopColor="#22c55e" stopOpacity="0.0" />
                            </linearGradient>
                        </defs>

                        {/* Gridlines */}
                        {ticks.map((val, idx) => {
                            const y = 200 - (170 * (val / roundedMax));
                            return (
                                <g key={idx}>
                                    <line x1="50" y1={y} x2="450" y2={y} className="analytics-chart-grid-line" />
                                    <text x="35" y={y + 4} className="analytics-chart-axis-text" textAnchor="end">
                                        {val.toFixed(0)}
                                    </text>
                                </g>
                            );
                        })}

                        {/* Fills */}
                        {orangeFillPath && <path d={orangeFillPath} fill={`url(#orangeGrad-${type})`} stroke="none" />}
                        {greenFillPath && <path d={greenFillPath} fill={`url(#greenGrad-${type})`} stroke="none" />}

                        {/* Lines */}
                        {orangePath && <path d={orangePath} fill="none" stroke="#E99924" strokeWidth="2" strokeLinecap="round" />}
                        {greenPath && <path d={greenPath} fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" />}

                        {/* Default End Badges/Callouts (visible when not hovering) */}
                        {hoverIndex === null && (
                            <>
                                {/* Orange Badge */}
                                <g>
                                    <rect x={Math.max(45, Math.min(455 - 35, lastPoint.x - 17.5))} y={Math.max(10, lastPoint.yBefore - 25)} width="35" height="20" rx="4" fill="#E99924" />
                                    <text x={Math.max(45, Math.min(455, lastPoint.x))} y={Math.max(24, lastPoint.yBefore - 11)} fill="#000" fontSize="11" fontWeight="bold" textAnchor="middle">
                                        {orangeEndVal}
                                    </text>
                                </g>

                                {/* Green Badge */}
                                <g>
                                    <rect x={Math.max(45, Math.min(455 - 35, lastPoint.x - 17.5))} y={Math.max(10, lastPoint.yAfter - 25)} width="35" height="20" rx="4" fill="#22c55e" />
                                    <text x={Math.max(45, Math.min(455, lastPoint.x))} y={Math.max(24, lastPoint.yAfter - 11)} fill="#fff" fontSize="11" fontWeight="bold" textAnchor="middle">
                                        {greenEndVal}
                                    </text>
                                </g>
                            </>
                        )}

                        {/* Live Tracker Crosshair & Circles */}
                        {hoverIndex !== null && points[hoverIndex] && (
                            <>
                                <line
                                    x1={points[hoverIndex].x}
                                    y1="30"
                                    x2={points[hoverIndex].x}
                                    y2="200"
                                    className="analytics-hover-line"
                                />
                                <circle
                                    cx={points[hoverIndex].x}
                                    cy={points[hoverIndex].yBefore}
                                    r="5"
                                    stroke="#E99924"
                                    className="analytics-hover-circle"
                                />
                                <circle
                                    cx={points[hoverIndex].x}
                                    cy={points[hoverIndex].yAfter}
                                    r="5"
                                    stroke="#22c55e"
                                    className="analytics-hover-circle"
                                />
                            </>
                        )}

                        {/* X-axis labels */}
                        {xLabels.map((lbl, idx) => (
                            <text key={idx} x={lbl.x} y="222" className="analytics-chart-axis-text" textAnchor="middle">
                                {lbl.text}
                            </text>
                        ))}
                    </svg>
                )}
            </div>

            {/* Float Tooltip Overlay */}
            {hoverIndex !== null && points[hoverIndex] && (
                <div
                    className="analytics-chart-tooltip"
                    style={{
                        left: `${tooltipPos.x}px`,
                        top: `${tooltipPos.y}px`
                    }}
                >
                    <div className="analytics-chart-tooltip-title">{getTooltipDate()}</div>
                    <div className="analytics-chart-tooltip-row">
                        <span className="analytics-chart-tooltip-label">Before:</span>
                        <span className="analytics-chart-tooltip-value" style={{ color: '#E99924' }}>
                            {(points[hoverIndex].pm_before || 0).toFixed(2)} µg/m³
                        </span>
                    </div>
                    <div className="analytics-chart-tooltip-row">
                        <span className="analytics-chart-tooltip-label">After:</span>
                        <span className="analytics-chart-tooltip-value" style={{ color: '#22c55e' }}>
                            {(points[hoverIndex].pm_after || 0).toFixed(2)} µg/m³
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};

export const Analytics = ({ setView }) => {
    const [user, setUser] = useState(() => {
        try {
            const stored = localStorage.getItem('user');
            return stored ? JSON.parse(stored) : null;
        } catch {
            return null;
        }
    });
    const [alert, setAlert] = useState(null);

    const [topPerformers, setTopPerformers] = useState(null);
    const [cleanestLocations, setCleanestLocations] = useState(null);
    const [airTreated30Days, setAirTreated30Days] = useState(null);
    const [pmTrend24Hours, setPmTrend24Hours] = useState(null);
    const [pmTrend7Days, setPmTrend7Days] = useState(null);
    const [pmTrend30Days, setPmTrend30Days] = useState(null);

    // Hover tooltip states for the 30-day air treated chart
    const [treatedHoverIndex, setTreatedHoverIndex] = useState(null);
    const [treatedTooltipPos, setTreatedTooltipPos] = useState({ x: 0, y: 0 });
    const treatedSvgRef = useRef(null);

    // Modal states for View All
    const [activeModal, setActiveModal] = useState(null); // 'top' or 'cleanest'
    const [modalData, setModalData] = useState([]);
    const [modalLoading, setModalLoading] = useState(false);

    const openModal = async (type) => {
        setActiveModal(type);
        setModalLoading(true);
        setModalData([]);
        try {
            const token = localStorage.getItem('accessToken');
            const headers = {
                'Authorization': token ? `Bearer ${token}` : '',
                'Content-Type': 'application/json'
            };
            const endpoint = type === 'top' ? 'top-performers' : 'cleanest-locations';
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/${endpoint}?limit=100000`, { headers });
            const json = await response.json();
            if (json && Array.isArray(json.data)) {
                setModalData(json.data);
            }
        } catch (error) {
            console.error(`Error loading modal data for ${type}:`, error);
        } finally {
            setModalLoading(false);
        }
    };

    const closeModal = () => {
        setActiveModal(null);
        setModalData([]);
    };


    const fetchUserDetails = async () => {
        try {
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) return null;
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user/AlluserDetails/`, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
            });
            if (!response.ok) throw new Error('Failed to fetch user details');
            const data = await response.json();
            if (data.status && data.data?.user) {
                const userData = data.data.user;
                if (userData.profile_picture && !userData.picture) userData.picture = userData.profile_picture;
                setUser(userData);
                localStorage.setItem('user', JSON.stringify(userData));
                return userData;
            }
            throw new Error('Invalid user data');
        } catch {
            const stored = localStorage.getItem('user');
            if (stored) { const u = JSON.parse(stored); setUser(u); return u; }
            return null;
        }
    };

    useEffect(() => {
        let isMounted = true;

        const loadUser = async () => {
            const userData = await fetchUserDetails();
            if (!userData && isMounted) {
                setAlert({ type: 'error', message: 'Error loading user information' });
                setTimeout(() => setView && setView('signin'), 500);
            }
        };

        const loadAnalyticsData = () => {
            const token = localStorage.getItem('accessToken');
            const headers = {
                'Authorization': token ? `Bearer ${token}` : '',
                'Content-Type': 'application/json'
            };

            // Fetch Top Performers
            fetch(`${process.env.REACT_APP_API_BASE_URL}/top-performers?limit=5`, { headers })
                .then(r => r.json())
                .then(res => {
                    if (isMounted) {
                        if (res && Array.isArray(res.data)) {
                            setTopPerformers(res.data);
                        } else {
                            setTopPerformers([]);
                        }
                    }
                })
                .catch(() => isMounted && setTopPerformers([]));

            // Fetch Cleanest Locations
            fetch(`${process.env.REACT_APP_API_BASE_URL}/cleanest-locations?limit=5`, { headers })
                .then(r => r.json())
                .then(res => {
                    if (isMounted) {
                        if (res && Array.isArray(res.data)) {
                            setCleanestLocations(res.data);
                        } else {
                            setCleanestLocations([]);
                        }
                    }
                })
                .catch(() => isMounted && setCleanestLocations([]));

            // Fetch Air Treated
            fetch(`${process.env.REACT_APP_API_BASE_URL}/air-treated-30-days`, { headers })
                .then(r => r.json())
                .then(res => {
                    if (isMounted) {
                        if (res && Array.isArray(res.data)) {
                            setAirTreated30Days(res.data);
                        } else {
                            setAirTreated30Days([]);
                        }
                    }
                })
                .catch(() => isMounted && setAirTreated30Days([]));

            // Fetch PM Trend 24h
            fetch(`${process.env.REACT_APP_API_BASE_URL}/pm-trend-24-hours`, { headers })
                .then(r => r.json())
                .then(res => {
                    if (isMounted) {
                        if (res && Array.isArray(res.data)) {
                            const mappedPm24 = res.data.map(item => ({
                                ...item,
                                report_hour: item.report_hour_local || item.report_hour_utc || item.report_hour
                            }));
                            setPmTrend24Hours(mappedPm24);
                        } else {
                            setPmTrend24Hours([]);
                        }
                    }
                })
                .catch(() => isMounted && setPmTrend24Hours([]));

            // Fetch PM Trend 7d
            fetch(`${process.env.REACT_APP_API_BASE_URL}/pm-trend-7-days`, { headers })
                .then(r => r.json())
                .then(res => {
                    if (isMounted) {
                        if (res && Array.isArray(res.data)) {
                            setPmTrend7Days(res.data);
                        } else {
                            setPmTrend7Days([]);
                        }
                    }
                })
                .catch(() => isMounted && setPmTrend7Days([]));

            // Fetch PM Trend 30d
            fetch(`${process.env.REACT_APP_API_BASE_URL}/pm-trend-30-days/`, { headers })
                .then(r => r.json())
                .then(res => {
                    if (isMounted) {
                        if (res && Array.isArray(res.data)) {
                            setPmTrend30Days(res.data);
                        } else {
                            setPmTrend30Days([]);
                        }
                    }
                })
                .catch(() => isMounted && setPmTrend30Days([]));
        };

        loadUser();
        loadAnalyticsData();

        // Dynamic Real-time polling: refresh graphs and tables every 60 seconds
        const pollInterval = setInterval(loadAnalyticsData, 60 * 1000);

        return () => {
            isMounted = false;
            clearInterval(pollInterval);
        };
    }, [setView]);


    // Shorten Pole ID to keep UX neat (e.g. AP-SSOAQ-U-26-IN1-B001-000001 -> Pole B001-1)
    const formatPoleId = (id) => {
        if (!id) return '';
        const parts = id.split('-');
        if (parts.length >= 6) {
            const b = parts[parts.length - 2];
            const s = parts[parts.length - 1];
            const shortSerial = s.replace(/^0+/, '') || '0';
            return `Pole ${b}-${shortSerial}`;
        }
        return id;
    };

    // Format treated volume from cubic feet to m³ with fallback rounding
    const formatVolume = (cuft) => {
        const m3 = (cuft || 0) * 0.0283168;
        if (m3 >= 1000000) return (m3 / 1000000).toFixed(2) + 'M m³';
        if (m3 >= 1000) return (m3 / 1000).toFixed(1) + 'K m³';
        return m3.toLocaleString(undefined, { maximumFractionDigits: 1 }) + ' m³';
    };

    // Helper to format date labels
    const formatDateLabel = (dateStr) => {
        if (!dateStr) return '';
        const parts = dateStr.split('-');
        if (parts.length < 3) return dateStr;
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const monthIdx = parseInt(parts[1], 10) - 1;
        const day = parseInt(parts[2], 10);
        return `${day} ${months[monthIdx]}`;
    };

    // Dynamic processing for Air Treated 30 Days SVG chart
    let airTreatedLinePath = '';
    let airTreatedFillPath = '';
    let airTreatedTicks = ['0', '1M', '2M', '3M', '4M'];
    let airTreatedMaxVal = 4000000;
    let airTreatedXLabels = [];
    let peakPoint = null;
    let airTreatedPoints = [];

    if (airTreated30Days && airTreated30Days.length > 0) {
        const processed = airTreated30Days.map(d => ({
            date: d.report_date,
            val: (d.air_treated_cuft || 0) * 0.0283168
        }));

        const maxVal = Math.max(...processed.map(p => p.val), 100);
        let roundedMax = 4000000;
        if (maxVal > 1000000) {
            roundedMax = Math.ceil(maxVal / 1000000) * 1000000;
            const step = roundedMax / 4;
            airTreatedTicks = [0, step, step * 2, step * 3, roundedMax].map(v => (v / 1000000).toFixed(1) + 'M');
        } else if (maxVal > 1000) {
            roundedMax = Math.ceil(maxVal / 1000) * 1000;
            const step = roundedMax / 4;
            airTreatedTicks = [0, step, step * 2, step * 3, roundedMax].map(v => (v / 1000).toFixed(0) + 'K');
        } else {
            roundedMax = Math.ceil(maxVal / 50) * 50;
            const step = roundedMax / 4;
            airTreatedTicks = [0, step, step * 2, step * 3, roundedMax].map(v => v.toFixed(0));
        }
        airTreatedMaxVal = roundedMax;

        airTreatedPoints = processed.map((p, idx) => {
            const x = 50 + (400 / (processed.length - 1 || 1)) * idx;
            const y = 190 - (160 * (p.val / roundedMax));
            return {
                x,
                y,
                val: p.val,
                date: p.date,
                rawCuft: airTreated30Days[idx].air_treated_cuft
            };
        });

        airTreatedLinePath = airTreatedPoints.map((p, idx) => (idx === 0 ? 'M' : 'L') + ` ${p.x},${p.y}`).join(' ');
        airTreatedFillPath = airTreatedPoints.length > 0 ? `${airTreatedLinePath} L ${airTreatedPoints[airTreatedPoints.length - 1].x},190 L ${airTreatedPoints[0].x},190 Z` : '';

        if (airTreatedPoints.length > 0) {
            peakPoint = airTreatedPoints[0];
            for (let i = 1; i < airTreatedPoints.length; i++) {
                if (airTreatedPoints[i].val > peakPoint.val) {
                    peakPoint = airTreatedPoints[i];
                }
            }
        }

        const N = processed.length;
        const indices = [0, Math.floor(N * 0.25), Math.floor(N * 0.5), Math.floor(N * 0.75), N - 1];
        const uniqueIndices = [...new Set(indices)].filter(idx => idx >= 0 && idx < N);
        airTreatedXLabels = uniqueIndices.map(idx => ({
            text: formatDateLabel(processed[idx].date),
            x: 50 + (400 / (N - 1 || 1)) * idx
        }));
    }

    const handleTreatedMouseMove = (e) => {
        if (!treatedSvgRef.current || airTreatedPoints.length === 0) return;
        const rect = treatedSvgRef.current.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        const localX = ((clientX - rect.left) / rect.width) * 500;

        const pct = (localX - 50) / 400;
        const idx = Math.max(0, Math.min(airTreatedPoints.length - 1, Math.round(pct * (airTreatedPoints.length - 1))));
        setTreatedHoverIndex(idx);

        const cardRect = e.currentTarget.getBoundingClientRect();
        setTreatedTooltipPos({
            x: clientX - cardRect.left,
            y: clientY - cardRect.top - 12
        });
    };

    const handleTreatedMouseLeave = () => {
        setTreatedHoverIndex(null);
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="page-root"
        >
            <InnerHeader
                user={user}
                setView={setView}
                onProfilePhotoSuccess={async () => {
                    setTimeout(async () => { await fetchUserDetails(); }, 500);
                    setAlert({ type: 'success', message: 'Profile picture updated successfully!' });
                    setTimeout(() => setAlert(null), 3000);
                }}
            />

            {alert && (
                <div className={`page-alert ${alert.type}`} style={{ margin: '20px 24px 0 24px' }}>
                    <span className="alert-message-flex">{alert.message}</span>
                </div>
            )}

            <PageTitleBar title="Analytics" setView={setView} activeBtn="analytics" />

            <div className="analytics-container">
                <div className="analytics-grid">
                    {/* 1. TOP PERFORMERS */}
                    <div className="analytics-card">
                        <div className="analytics-card-header">
                            <div className="analytics-card-title-group">
                                <h3 className="analytics-card-title">TOP PERFORMERS</h3>
                                <span className="analytics-card-subtitle">By Air Treated Today</span>
                            </div>
                            <button className="analytics-btn-small" onClick={() => openModal('top')}>View All</button>
                        </div>
                        <div className="analytics-list">
                            {topPerformers === null ? (
                                <div className="skeleton-loader">
                                    <div className="skeleton-bar" style={{ width: '80%', height: '14px' }}></div>
                                    <div className="skeleton-bar" style={{ width: '90%', height: '14px' }}></div>
                                    <div className="skeleton-bar" style={{ width: '70%', height: '14px' }}></div>
                                    <div className="skeleton-bar" style={{ width: '85%', height: '14px' }}></div>
                                    <div className="skeleton-bar" style={{ width: '60%', height: '14px' }}></div>
                                </div>
                            ) : topPerformers.length === 0 ? (
                                <div style={{ color: '#8a99ad', fontSize: 13, textAlign: 'center', marginTop: 40 }}>No pole telemetry synced today</div>
                            ) : (
                                topPerformers.slice(0, 5).map((pole, idx) => (
                                    <div key={idx} className="analytics-list-item">
                                        <span className="analytics-item-name">{formatPoleId(pole.pole_id)}</span>
                                        <div className="analytics-item-value-wrap">
                                            <span className="analytics-item-value">{formatVolume(pole.air_treated_cuft)}</span>
                                            <span className="analytics-trend-indicator analytics-trend-up">
                                                <ArrowUp size={14} style={{ marginLeft: 2 }} />
                                            </span>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* 2. CLEANEST LOCATIONS */}
                    <div className="analytics-card">
                        <div className="analytics-card-header">
                            <div className="analytics-card-title-group">
                                <h3 className="analytics-card-title">CLEANEST LOCATIONS</h3>
                                <span className="analytics-card-subtitle">By PM After Filtration - PM [ug/m³]</span>
                            </div>
                            <button className="analytics-btn-small" onClick={() => openModal('cleanest')}>View All</button>
                        </div>
                        <div className="analytics-list">
                            {cleanestLocations === null ? (
                                <div className="skeleton-loader">
                                    <div className="skeleton-bar" style={{ width: '85%', height: '14px' }}></div>
                                    <div className="skeleton-bar" style={{ width: '70%', height: '14px' }}></div>
                                    <div className="skeleton-bar" style={{ width: '90%', height: '14px' }}></div>
                                    <div className="skeleton-bar" style={{ width: '65%', height: '14px' }}></div>
                                    <div className="skeleton-bar" style={{ width: '75%', height: '14px' }}></div>
                                </div>
                            ) : cleanestLocations.length === 0 ? (
                                <div style={{ color: '#8a99ad', fontSize: 13, textAlign: 'center', marginTop: 40 }}>No location telemetry found</div>
                            ) : (
                                cleanestLocations.slice(0, 5).map((loc, idx) => (
                                    <div key={idx} className="analytics-list-item">
                                        <span className="analytics-item-name" title={loc.location_name} style={{
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            maxWidth: '200px'
                                        }}>{loc.location_name}</span>
                                        <div className="analytics-item-value-wrap">
                                            <span className="analytics-item-value">{(loc.pm_after_filtration || 0).toFixed(1)}</span>
                                            <span className="analytics-trend-indicator analytics-trend-neutral" style={{ fontWeight: 'bold' }}>
                                                +
                                            </span>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* 3. AIR TREATED OVER 30 DAYS */}
                    <div
                        className="analytics-card"
                        onMouseMove={handleTreatedMouseMove}
                        onTouchMove={handleTreatedMouseMove}
                        onMouseLeave={handleTreatedMouseLeave}
                        onTouchEnd={handleTreatedMouseLeave}
                    >
                        <div className="analytics-card-header">
                            <div className="analytics-card-title-group">
                                <h3 className="analytics-card-title" style={{ textTransform: 'unset' }}>AIR TREATED OVER 30 DAYS (m³)</h3>
                            </div>
                        </div>
                        <div className="analytics-chart-container">
                            {airTreated30Days === null ? (
                                <div className="skeleton-loader">
                                    <div className="skeleton-chart"></div>
                                </div>
                            ) : airTreated30Days.length === 0 ? (
                                <div style={{ color: '#8a99ad', fontSize: 13, textAlign: 'center', marginTop: 60 }}>No historical treated air data found</div>
                            ) : (
                                <svg ref={treatedSvgRef} viewBox="0 0 500 240" className="analytics-chart-svg" preserveAspectRatio="none">
                                    <defs>
                                        <linearGradient id="yellowGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#E99924" stopOpacity="0.4" />
                                            <stop offset="100%" stopColor="#E99924" stopOpacity="0.0" />
                                        </linearGradient>
                                    </defs>

                                    {/* Gridlines */}
                                    {airTreatedTicks.map((tickText, idx) => {
                                        const y = 190 - (160 * (idx / 4));
                                        return (
                                            <g key={idx}>
                                                <line x1="50" y1={y} x2="450" y2={y} className="analytics-chart-grid-line" />
                                                <text x="35" y={y + 4} className="analytics-chart-axis-text" textAnchor="end">
                                                    {tickText}
                                                </text>
                                            </g>
                                        );
                                    })}

                                    {/* Fill */}
                                    {airTreatedFillPath && (
                                        <path
                                            d={airTreatedFillPath}
                                            fill="url(#yellowGrad)"
                                            stroke="none"
                                        />
                                    )}

                                    {/* Line */}
                                    {airTreatedLinePath && (
                                        <path
                                            d={airTreatedLinePath}
                                            fill="none"
                                            stroke="#E99924"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                        />
                                    )}

                                    {/* Peak Highlight Badge (visible when not hovering) */}
                                    {treatedHoverIndex === null && peakPoint && (
                                        <g>
                                            <rect
                                                x={Math.max(45, Math.min(455 - 75, peakPoint.x - 37.5))}
                                                y={Math.max(10, peakPoint.y - 28)}
                                                width="75"
                                                height="20"
                                                rx="4"
                                                fill="#E99924"
                                            />
                                            <text
                                                x={Math.max(45, Math.min(455, peakPoint.x))}
                                                y={Math.max(24, peakPoint.y - 14)}
                                                fill="#000"
                                                fontSize="10"
                                                fontWeight="bold"
                                                textAnchor="middle"
                                            >
                                                {formatVolume(peakPoint.val / 0.0283168)}
                                            </text>
                                        </g>
                                    )}

                                    {/* Live Hover Crosshair Line & Circle */}
                                    {treatedHoverIndex !== null && airTreatedPoints[treatedHoverIndex] && (
                                        <>
                                            <line
                                                x1={airTreatedPoints[treatedHoverIndex].x}
                                                y1="30"
                                                x2={airTreatedPoints[treatedHoverIndex].x}
                                                y2="190"
                                                className="analytics-hover-line"
                                            />
                                            <circle
                                                cx={airTreatedPoints[treatedHoverIndex].x}
                                                cy={airTreatedPoints[treatedHoverIndex].y}
                                                r="5"
                                                stroke="#E99924"
                                                className="analytics-hover-circle"
                                            />
                                        </>
                                    )}

                                    {/* X-axis labels */}
                                    {airTreatedXLabels.map((lbl, idx) => (
                                        <text key={idx} x={lbl.x} y="215" className="analytics-chart-axis-text" textAnchor="middle">
                                            {lbl.text}
                                        </text>
                                    ))}
                                </svg>
                            )}
                        </div>

                        {/* Live Tooltip Overlay */}
                        {treatedHoverIndex !== null && airTreatedPoints[treatedHoverIndex] && (
                            <div
                                className="analytics-chart-tooltip"
                                style={{
                                    left: `${treatedTooltipPos.x}px`,
                                    top: `${treatedTooltipPos.y}px`
                                }}
                            >
                                <div className="analytics-chart-tooltip-title">{formatDateLabel(airTreatedPoints[treatedHoverIndex].date)}</div>
                                <div className="analytics-chart-tooltip-row">
                                    <span className="analytics-chart-tooltip-label">Treated:</span>
                                    <span className="analytics-chart-tooltip-value" style={{ color: '#E99924' }}>
                                        {formatVolume(airTreatedPoints[treatedHoverIndex].rawCuft)}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* 4. PM-BASED AIR QUALITY TREND [24 HOURS] */}
                    <TrendChart
                        title="PM-BASED AIR QUALITY TREND"
                        timeframe="24 HOURS"
                        data={pmTrend24Hours}
                        type="24h"
                    />

                    {/* 5. PM-BASED AIR QUALITY TREND [1 WEEK] */}
                    <TrendChart
                        title="PM-BASED AIR QUALITY TREND"
                        timeframe="1 WEEK"
                        data={pmTrend7Days}
                        type="7d"
                    />

                    {/* 6. PM-BASED AIR QUALITY TREND [30 DAYS] */}
                    <TrendChart
                        title="PM-BASED AIR QUALITY TREND"
                        timeframe="30 DAYS"
                        data={pmTrend30Days}
                        type="30d"
                    />
                </div>
            </div>
            {activeModal && (
                <div className="analytics-modal-backdrop" onClick={closeModal}>
                    <div className="analytics-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="analytics-modal-header">
                            <h3 className="analytics-modal-title">
                                {activeModal === 'top' ? 'TOP PERFORMERS' : 'CLEANEST LOCATIONS'}
                            </h3>
                            <button className="analytics-modal-close" onClick={closeModal} aria-label="Close">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="analytics-modal-body">
                            {modalLoading ? (
                                <div className="analytics-modal-loading">Loading data...</div>
                            ) : modalData.length === 0 ? (
                                <div className="analytics-modal-empty">No data available</div>
                            ) : (
                                <table className="analytics-modal-table">
                                    <thead>
                                        {activeModal === 'top' ? (
                                            <tr>
                                                <th>Rank</th>
                                                <th>Pole ID</th>
                                                <th>Air Treated (Cubic Feet)</th>
                                                <th>Air Treated (m³)</th>
                                            </tr>
                                        ) : (
                                            <tr>
                                                <th>Rank</th>
                                                <th>Location</th>
                                                <th>PM After Filtration (µg/m³)</th>
                                            </tr>
                                        )}
                                    </thead>
                                    <tbody>
                                        {modalData.map((item, idx) => (
                                            <tr key={idx}>
                                                <td>{idx + 1}</td>
                                                {activeModal === 'top' ? (
                                                    <>
                                                        <td className="pole-id-cell" title={item.pole_id}>
                                                            {formatPoleId(item.pole_id)}
                                                        </td>
                                                        <td>{(item.air_treated_cuft ?? 0).toLocaleString()}</td>
                                                        <td>{formatVolume(item.air_treated_cuft)}</td>
                                                    </>
                                                ) : (
                                                    <>
                                                        <td>{item.location_name || 'N/A'}</td>
                                                        <td className="pm-value-cell">
                                                            {(item.pm_after_filtration ?? 0).toFixed(1)}
                                                        </td>
                                                    </>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            )}
            <img src={footerImgNew} alt="" style={{ width: '100%', height: 'auto' }} />
            <InnerFooter />
        </motion.div>
    );
};

export default Analytics;
